/**
 * Created by piotr.pozniak@thebeaverhead.com on 02/03/2021.
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import CardEvent from "./CardEvent";
import LoadingIndicator from "../LoadingIndicator";
import Slider from "react-slick";
import { CT, PT } from "../../../../consts";
import PoweredBy from "../PoweredBy";
import CalendarHeader from "../CalendarHeader";
import SearchInput from "../AiSearch/SearchInput";
import useAiSearch from "../../../../hooks/useAiSearch";
import WidgetSettingsContext from "../../../../contexts/WidgetSettingsContext";
import LoaderIndicatorSlider from "./LoaderIndicatorSlider";
import useWindowSize from "../../../../hooks/useWindowSize";
import { useDebounce } from "../../../../hooks/useDebounce";

const CardSlider = (props) => {
  const calendarID = "dce_calendar__" + props.calendar.model.uuid;

  const [state, setState] = useState({
    swiping: false,
    currentSlide: 0,
    currentPage: 1,
    windowWidth: 0,
    isMobileWidth: false,
  });

  const [resetSliderPosition, setResetSliderPosition] = useState(false);

  /**
   *
   * @param width
   * @returns {boolean}
   */
  const checkIfMobileWidth = (width) => {
    return width < 700;
  };

  const isMobileWidth = useRef(false);
  const { calendar, events } = props;

  const { hasAIEnabled } = useAiSearch(calendar.model.integration);
  const widgetSettings = useContext(WidgetSettingsContext);
  const { t } = useTranslation();

  const slickRef = useRef();
  const sliderContainerRef = useRef();

  // With `loop` on, react-slick clones the lead/trail slides for the
  // wraparound illusion and marks them `aria-hidden="true"`, but it doesn't
  // stop their focusable descendants (card links, CTA buttons) from being
  // Tab-reachable - landing focus inside an aria-hidden branch hides it from
  // screen readers while a sighted keyboard user can still activate it.
  // Mirror each slide's aria-hidden state onto its focusable descendants'
  // tabIndex so hidden slides are skipped by Tab too.
  useEffect(() => {
    const container = sliderContainerRef.current;

    if (!container) {
      return;
    }

    const FOCUSABLE_SELECTOR =
      "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])";

    const syncSlide = (slide) => {
      const hidden = slide.getAttribute("aria-hidden") === "true";

      slide.querySelectorAll(FOCUSABLE_SELECTOR).forEach((el) => {
        if (hidden) {
          el.setAttribute("tabindex", "-1");
        } else {
          el.removeAttribute("tabindex");
        }
      });
    };

    const syncAll = () => {
      container
        .querySelectorAll(".slick-slide")
        .forEach((slide) => syncSlide(slide));
    };

    syncAll();

    // `childList` matters as much as `attributes` here: slides aren't just
    // toggled aria-hidden in place, new slide nodes (e.g. once events finish
    // loading and real cards replace the loader) show up as childList
    // mutations, which an attributes-only observer would never see.
    const observer = new MutationObserver(syncAll);

    observer.observe(container, {
      attributes: true,
      attributeFilter: ["aria-hidden"],
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  const loading =
    calendar.rsvp ||
    calendar.fetch ||
    calendar.delete ||
    calendar.update ||
    events.fetch;

  const inpicDatePlacement = widgetSettings["cwDateLocation"];

  useEffect(() => {
    const calendarWrapper = document.querySelector("." + calendarID);

    if (props.previewType === PT.desktop) {
      isMobileWidth.current = false;
      setState({ ...state, isMobileWidth: false });
    } else if (props.previewType === PT.mobile) {
      isMobileWidth.current = true;
      setState({ ...state, isMobileWidth: true });
    } else {
      if (calendarWrapper) {
        isMobileWidth.current = checkIfMobileWidth(calendarWrapper.clientWidth);
        setState({
          ...state,
          //  isMobileWidth: checkIfMobileWidth(calendarWrapper.clientWidth),
        });
      } else {
        isMobileWidth.current = false;
        setState({ ...state, isMobileWidth: false });
      }
    }
  }, [state.windowWidth, props.previewType]);

  const showTitle = Number.parseInt(widgetSettings["ccShowTitle"]) === 1;
  const showDate = Number.parseInt(widgetSettings["ccShowDate"]) === 1;
  const showLocation = Number.parseInt(widgetSettings["ccShowLocation"]) === 1;
  const showDescription =
    Number.parseInt(widgetSettings["ccShowDescription"]) === 1;
  const showSummary = Number.parseInt(widgetSettings["ccShowSummary"]) === 1;
  const hoverEffect = widgetSettings["cwHoverEffect"];
  const showDots = Number.parseInt(widgetSettings["sliderShowDots"]) === 1;
  const centerSlides =
    Number.parseInt(widgetSettings["sliderCenterSlides"]) === 1;
  const loop = Number.parseInt(widgetSettings["sliderLoop"]) === 1;
  const autoPlay = Number.parseInt(widgetSettings["sliderAutoPlay"]) === 1;
  const autoplaySpeed = widgetSettings["sliderAutoplaySpeed"];
  const sliderSlidesToShowValue = widgetSettings["sliderSlidesToShow"] * 1;
  const numberOfSlides = isMobileWidth.current /*state.isMobileWidth*/
    ? 1
    : sliderSlidesToShowValue;
  const sliderNextSlideToShow =
    Number.parseInt(widgetSettings["sliderNextSlideToShow"]) * 0.1;
  const dotsSize = widgetSettings["sliderSizeOfDots"];
  const showWeekDayInList =
    Number.parseInt(widgetSettings["eventDetailsShowDayOfWeek"]) === 1;
  const weekDayFormatter = widgetSettings["eventsShowDayWeekOption"];
  const hasInfiniteScroll =
    Number.parseInt(widgetSettings["infiniteScroll"]) === 1;
  const useEventAccent =
    Number.parseInt(widgetSettings["generalUseEventAccent"]) === 1;
  const showImage =
    Number.parseInt(widgetSettings["eventDetailsShowImage"]) === 1;

  const recurringShowBadge =
    Number.parseInt(widgetSettings["recurringShowBadge"]) === 1;
  const featuredShowBadge =
    Number.parseInt(widgetSettings["featuredShowBadge"]) === 1;

  const showCardCta = Number.parseInt(widgetSettings["cCardShowCta1st"]) === 1;

  const badgesLocation = widgetSettings["recurringBadgeLocation"];
  const selectedDateTimeFormat = widgetSettings["ccShowTimeOptions"];
  const showImageContainer =
    Number.parseInt(widgetSettings["eventDetailsShowImageContainer"]) === 1;

  const triggerCTAOnCardClick =
    Number.parseInt(widgetSettings["cCardActionOnClick"]) === 1;

  const showAccentColorOnCard =
    Number.parseInt(widgetSettings["cwShowAccentBar"]) === 1;

  const watermark =
    calendar.model && calendar.model.is_watermarked ? (
      <PoweredBy align={hasInfiniteScroll ? "right" : "left"} />
    ) : null;

  useEffect(() => {
    if (hasInfiniteScroll) {
      props.onLoadMore(state.currentPage);
    }
  }, [state.currentPage]);

  useEffect(() => {
    if (state.currentSlide === -1) {
      setTimeout(() => slickRef.current.slickGoTo(0, true), 500);
    }
  }, [state.currentSlide]);

  useEffect(() => {
    if (!events.collection.length) {
      setResetSliderPosition(true);
    }

    if (events.collection.length && resetSliderPosition) {
      slickRef.current.slickGoTo(0, true);
      setResetSliderPosition(false);
    }
  }, [
    events.fetchSuccess,
    events.collection,
    slickRef.current,
    resetSliderPosition,
  ]);

  const _windowSize = useWindowSize();

  const windowSize = useDebounce(_windowSize, 200);

  useEffect(() => {
    if (state.windowWidth === windowSize.width || !windowSize.width) {
      return;
    }

    console.log("window size changed", windowSize.width);
    setState({
      ...state,
      windowWidth: windowSize.width,
    });
  }, [windowSize]);

  const onBlockClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    },
    [state.swiping]
  );

  const cardEvents = /*1
    ? []
    :*/ events.collection.map((i, idx) => {
    return (
      <div key={i.slug} className={"card-slider_card-wrapper"}>
        <CardEvent
          event={i}
          onCardClick={state.swiping ? onBlockClick : null}
          inpicDatePlacement={inpicDatePlacement}
          showDate={showDate}
          showTitle={showTitle}
          showLocation={showLocation}
          showDescription={showDescription}
          showSummary={showSummary}
          hoverEffect={hoverEffect}
          embedded={props.embedded}
          showWeekDay={showWeekDayInList}
          weekDayFormatter={weekDayFormatter}
          useEventAccent={useEventAccent}
          showImage={showImage}
          showRecurringBadge={recurringShowBadge}
          showFeaturedBadge={featuredShowBadge}
          badgesLocation={badgesLocation}
          showCardCta={showCardCta}
          selectedDateTimeFormat={selectedDateTimeFormat}
          showImageContainer={showImageContainer}
          triggerCTAOnCardClick={triggerCTAOnCardClick}
          showAccentOnCard={showAccentColorOnCard}
        />
      </div>
    );
  });

  const loadingIndicator = /* 1 || */ loading ? (
    <LoaderIndicatorSlider />
  ) : null;
  if (loadingIndicator) {
    cardEvents.splice(-1, 0, loadingIndicator);
  }

  // number of dots based on number of events, not less than 10
  const dotsDividingFactor = !centerSlides ? numberOfSlides : 1;
  const eventsLengthForDots =
    events.collection.length / dotsDividingFactor < 10
      ? events.collection.length
      : 10;

  const numberOfDots = Math.ceil(eventsLengthForDots / dotsDividingFactor);
  const halfDots = Math.floor(numberOfDots / 2);

  /**
   *
   */
  const sliderOnSwipe = useCallback(() => {
    setState({ ...state, swiping: true });
  }, [state]);

  /**
   *
   * @param currentIndex
   * @param nextIndex sometimes the slider returns next index as float, it needs
   * to be ceiled before use.
   */
  const sliderBeforeChange = useCallback(
    (currentIndex, nextIndex) => {
      // console.log("beforeChange dots", currentIndex, nextIndex);
      nextIndex = Math.ceil(nextIndex);

      let page = state.currentPage;

      if (hasInfiniteScroll && nextIndex >= events.collection.length - 3) {
        page++;
      }

      setTimeout(
        () =>
          setState({
            ...state,
            swiping: false,
            currentSlide: Math.ceil(
              nextIndex / (!centerSlides ? numberOfSlides : 1)
            ),
            currentPage: page,
          }),
        200
      );
    },
    [state, hasInfiniteScroll, events.collection, centerSlides, numberOfSlides]
  );

  const sliderAppendDots = (dots) => {
    if (loadingIndicator) {
      return (
        <div>
          <div className={"slider-loading-indicator"}>
            <LoadingIndicator />
          </div>
        </div>
      );
    }

    const updatedDots = dots.map((i, idx) => {
      const isActive = idx == state.currentSlide;

      return (
        <li
          key={"sdts-" + idx}
          className={classnames("slick-dots-" + dotsSize, {
            "slick-active": isActive,
          })}
        >
          {React.cloneElement(i.props.children, {
            "aria-label": t("Go to slide {{number}}", { number: idx + 1 }),
            "aria-current": isActive ? "true" : undefined,
          })}
        </li>
      );
    });

    let leftPosition = 0;

    // move only if selected slide is further than a half of visible dots
    if (state.currentSlide > halfDots) {
      // maximum movement, it wil not go further than twice the half of visible dots -1
      // - - - | * * * * O * * *
      //                 ^ won't go further than here
      //
      let maxMovementFactor = dots.length - halfDots * 2 - 1;
      maxMovementFactor = maxMovementFactor < 0 ? 0 : maxMovementFactor;

      // how many dots to move, current slide - half of the visible dots
      const currentMovement = state.currentSlide - halfDots;

      leftPosition =
        -(currentMovement > maxMovementFactor
          ? maxMovementFactor
          : currentMovement) * 30;
    }

    return (
      <div style={{ width: numberOfDots * 30 + "px" }}>
        <ul
          style={{
            left: leftPosition + "px",
          }}
        >
          {updatedDots}
        </ul>
      </div>
    );
  };

  const sliderSettings = useMemo(
    () => ({
      dots: showDots,
      infinite: loop && events.collection.length >= numberOfSlides,
      autoplay: autoPlay,
      autoplaySpeed: autoplaySpeed * 1000,
      speed: 500,
      slidesToShow: numberOfSlides + sliderNextSlideToShow,
      slidesToScroll: numberOfSlides,
      arrows: false,
      accessibility: true,
      rows: 1,
      fade: false,
      centerPadding: centerSlides ? "60px" : "25px",
      centerMode: centerSlides,
      onSwipe: sliderOnSwipe,
      beforeChange: sliderBeforeChange,
      appendDots: sliderAppendDots,
    }),
    [
      showDots,
      loop,
      autoPlay,
      numberOfSlides,
      autoplaySpeed,
      centerSlides,
      sliderOnSwipe,
      sliderBeforeChange,
      sliderAppendDots,
      state,
      t,
    ]
  );

  //slick-active

  const search =
    hasAIEnabled && widgetSettings["aiEnabled"] ? (
      <SearchInput
        searchEvents={props.searchEvents}
        fetchEvents={props.fetchEvents}
      />
    ) : null;

  return (
    <div
      className={classnames(
        "calendar-card-view",
        `dce--${CT.cardSlider}`,
        calendarID,
        {
          "mobile-preview": props.previewType == PT.mobile,
        }
      )}
    >
      <CalendarHeader
        disabled={events.fetch}
        calendar={calendar}
        currentDate={props.selectedDate}
        onChangeDate={props.onSelectedDateChange}
        events={events.collection}
        locations={events.locations}
        onUpcomingNavigationChange={props.onUpcomingNavigationChange}
        selectedFilters={props.selectedFilters}
        onChangeFilter={props.onChangeFilter}
        onSearch={props.onKeywordSearch}
      />
      {search}
      <div ref={sliderContainerRef}>
        <Slider
          {...sliderSettings}
          className={"card-view-slider"}
          ref={slickRef}
        >
          {loadingIndicator}
          {cardEvents}
        </Slider>
      </div>
      {watermark}
    </div>
  );
};

CardSlider.propTypes = {
  calendar: PropTypes.object,
  events: PropTypes.object,
  onSelectedDateChange: PropTypes.func.isRequired,
  onUpcomingNavigationChange: PropTypes.func.isRequired,
  selectedDate: PropTypes.object,
  initialized: PropTypes.bool,
  embedded: PropTypes.bool,
  previewType: PropTypes.oneOf(Object.values(PT)),
  onLoadMore: PropTypes.func.isRequired,
  onChangeFilter: PropTypes.func.isRequired,
  onKeywordSearch: PropTypes.func.isRequired,
  selectedFilters: PropTypes.object.isRequired,
};

export default CardSlider;
